import "leaflet/dist/leaflet.css";

import L from "leaflet";
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
} from "react";
import {
  MapContainer,
  Marker,
  TileLayer,
  useMap,
  useMapEvents,
} from "react-leaflet";
import { createGlobalStyle } from "styled-components";

import {
  CATEGORY_COLORS,
  OSM_TILE_LAYERS,
  regionToZoom,
  type MapRegion,
  type OsmTileStyle,
} from "../../constants";
import type { ClusterMode, MapClusterItem } from "../../hooks/useMapClusters";
import type { MapPoiCategory } from "../../services/map.types";
import { fontSizes } from "../../../../shared/styles/typography";

const MapLeafletDarkStyles = createGlobalStyle`
  .map-leaflet--dark .leaflet-tile-pane {
    filter: invert(1) hue-rotate(180deg) brightness(0.9) contrast(0.95);
  }

  .map-leaflet--dark.leaflet-container {
    background: #1a1d24;
  }
`;

export type MapLeafletRef = {
  animateToCoordinate: (
    latitude: number,
    longitude: number,
    latitudeDelta: number
  ) => void;
};

type MapLeafletProps = {
  initialLatitude: number;
  initialLongitude: number;
  initialLatitudeDelta: number;
  tileStyle: OsmTileStyle;
  clusterItems: MapClusterItem[];
  clusterMode: ClusterMode;
  showCategoryClusterIcons: boolean;
  selectedMarkerId?: string;
  userLatitude?: number;
  userLongitude?: number;
  onMarkerPress: (id: string) => void;
  onClusterPress: (
    clusterId: number,
    latitude: number,
    longitude: number,
    category?: MapPoiCategory
  ) => void;
  onMapStateChange: (region: MapRegion) => void;
  onMapPress: () => void;
};

function boundsToRegion(bounds: L.LatLngBounds): MapRegion {
  const center = bounds.getCenter();
  const ne = bounds.getNorthEast();
  const sw = bounds.getSouthWest();
  return {
    latitude: center.lat,
    longitude: center.lng,
    latitudeDelta: ne.lat - sw.lat,
    longitudeDelta: ne.lng - sw.lng,
  };
}

function MapController({ mapRef }: { mapRef: React.Ref<MapLeafletRef> }) {
  const map = useMap();

  useImperativeHandle(mapRef, () => ({
    animateToCoordinate(latitude, longitude, latitudeDelta) {
      map.flyTo([latitude, longitude], regionToZoom(latitudeDelta), {
        duration: 0.6,
      });
    },
  }));

  return null;
}

function MapEvents({
  onMapStateChange,
  onMapPress,
}: {
  onMapStateChange: (region: MapRegion) => void;
  onMapPress: () => void;
}) {
  const map = useMap();

  useEffect(() => {
    onMapStateChange(boundsToRegion(map.getBounds()));
  }, [map, onMapStateChange]);

  useMapEvents({
    moveend: () => onMapStateChange(boundsToRegion(map.getBounds())),
    click: () => onMapPress(),
  });

  return null;
}

/** Corrige tiles cinza quando o container muda de tamanho (tab bar, resize). */
function InvalidateSize() {
  const map = useMap();

  useEffect(() => {
    const id = window.setTimeout(() => {
      map.invalidateSize();
    }, 80);
    const onResize = () => map.invalidateSize();
    window.addEventListener("resize", onResize);
    return () => {
      window.clearTimeout(id);
      window.removeEventListener("resize", onResize);
    };
  }, [map]);

  return null;
}

function createPinIcon(color: string, selected = false) {
  const size = selected ? 36 : 28;
  const border = selected ? 3 : 2;
  return L.divIcon({
    className: "",
    iconSize: [size, size],
    iconAnchor: [size / 2, size],
    html: `<div style="
      width:${size}px;height:${size}px;
      background:${color};
      border:${border}px solid #fff;
      border-radius:50% 50% 50% 0;
      transform:rotate(-45deg);
      box-shadow:0 2px 6px rgba(0,0,0,0.25);
    "></div>`,
  });
}

function getClusterSize(count: number): number {
  if (count > 99) return 44;
  if (count > 9) return 40;
  return 36;
}

function createClusterIcon(count: number, color: string) {
  const size = getClusterSize(count);
  return L.divIcon({
    className: "",
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
    html: `<div style="
      width:${size}px;height:${size}px;
      background:${color};
      color:#fff;
      border:2px solid #fff;
      border-radius:50%;
      display:flex;align-items:center;justify-content:center;
      font-size:${fontSizes.sm}px;font-weight:700;
      box-shadow:0 2px 8px rgba(0,0,0,0.3);
    ">${count > 999 ? "999+" : count}</div>`,
  });
}

function createUserIcon() {
  return L.divIcon({
    className: "",
    iconSize: [18, 18],
    iconAnchor: [9, 9],
    html: `<div style="
      width:18px;height:18px;
      background:#3B82F6;
      border:3px solid #fff;
      border-radius:50%;
      box-shadow:0 0 0 6px rgba(59,130,246,0.25);
    "></div>`,
  });
}

function createCategoryClusterIcon(category: MapPoiCategory) {
  const color = CATEGORY_COLORS[category];
  return L.divIcon({
    className: "",
    iconSize: [40, 40],
    iconAnchor: [20, 20],
    html: `<div style="
      width:40px;height:40px;
      background:${color}22;
      border:2px solid ${color};
      border-radius:50%;
    "></div>`,
  });
}

export const MapLeaflet = forwardRef<MapLeafletRef, MapLeafletProps>(
  function MapLeaflet(
    {
      initialLatitude,
      initialLongitude,
      initialLatitudeDelta,
      tileStyle,
      clusterItems,
      clusterMode,
      showCategoryClusterIcons,
      selectedMarkerId,
      userLatitude,
      userLongitude,
      onMarkerPress,
      onClusterPress,
      onMapStateChange,
      onMapPress,
    },
    ref
  ) {
    const initialZoom = regionToZoom(initialLatitudeDelta);
    const tile = OSM_TILE_LAYERS[tileStyle];
    const userIcon = useMemo(() => createUserIcon(), []);
    const invertTiles =
      "invertTiles" in tile ? Boolean(tile.invertTiles) : false;

    return (
      <>
        <MapLeafletDarkStyles />
        <MapContainer
          center={[initialLatitude, initialLongitude]}
          zoom={initialZoom}
          style={{
            width: "100%",
            height: "100%",
            background: invertTiles ? "#1A1D24" : "#E8EDF2",
          }}
          className={invertTiles ? "map-leaflet--dark" : undefined}
          zoomControl={false}
          attributionControl={false}
        >
          <MapController mapRef={ref} />
          <InvalidateSize />
          <TileLayer
            key={tileStyle}
            url={tile.url}
            maxZoom={tile.maxZoom}
            attribution={tile.attribution}
            subdomains={tile.subdomains}
          />
          <MapEvents
            onMapStateChange={onMapStateChange}
            onMapPress={onMapPress}
          />

          {userLatitude != null && userLongitude != null ? (
            <Marker
              position={[userLatitude, userLongitude]}
              icon={userIcon}
              interactive={false}
            />
          ) : null}

          {clusterItems.map((item) => {
            if (item.type === "cluster") {
              const color =
                item.category != null
                  ? CATEGORY_COLORS[item.category]
                  : "#5F33E1";
              const icon =
                clusterMode === "category" &&
                showCategoryClusterIcons &&
                item.category
                  ? createCategoryClusterIcon(item.category)
                  : createClusterIcon(item.pointCount, color);

              return (
                <Marker
                  key={`cluster-${item.clusterId}-${item.category ?? "all"}`}
                  position={[item.latitude, item.longitude]}
                  icon={icon}
                  eventHandlers={{
                    click: (e) => {
                      L.DomEvent.stopPropagation(e.originalEvent);
                      onClusterPress(
                        item.clusterId,
                        item.latitude,
                        item.longitude,
                        item.category
                      );
                    },
                  }}
                />
              );
            }

            const { poi } = item;
            const isSelected = selectedMarkerId === poi.id;
            const pinColor = isSelected
              ? "#2D0D78"
              : CATEGORY_COLORS[poi.category];

            return (
              <Marker
                key={poi.id}
                position={[poi.latitude, poi.longitude]}
                icon={createPinIcon(pinColor, isSelected)}
                eventHandlers={{
                  click: (e) => {
                    L.DomEvent.stopPropagation(e.originalEvent);
                    onMarkerPress(poi.id);
                  },
                }}
              />
            );
          })}
        </MapContainer>
      </>
    );
  }
);
